import { byLogOrder } from "@storyboard/dispatch-view/acknowledgement";
import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { ProjectedRecord } from "@storyboard/driver-query/predicate";
import type { DriverRow } from "@/types/driver";
import type { DispatchItem } from "./dispatchItems";
import type { TaskFold } from "./tasks";

/**
 * One Dispatch table row at either grain: a driver, one message on a driver's channel, or one open task on the shelf
 * at the message grain. Each carries the driver and a flattened record, projected once so filters, sort, and cells
 * agree.
 *
 * `id` is what the table, the highlight, and the expanded row key on: the membership id or the item id, except when
 * grouping lands one row under several groups and each copy needs its own. A driver row carries `unacknowledgedCount`,
 * how many of the driver's messages wait on a dispatcher (zero when the channel is handled), beside the driver's read
 * state; a message row's item carries both for the message. A task row carries the task, the driver it bubbles up to,
 * and the message or event it is about when it has one; reply, History, and Context open on that driver's channel.
 * The store calls a message an `Item`, so the message row holds a `DispatchItem`.
 */
export type DispatchTableRow =
  | { kind: "driver"; id: string; driver: DriverRow; unacknowledgedCount: number; record: ProjectedRecord }
  | { kind: "message"; id: string; item: DispatchItem; driver: DriverRow; record: ProjectedRecord }
  | { kind: "task"; id: string; task: OpenTask; driver: DriverRow; item?: DispatchItem; record: ProjectedRecord };

/** Whether the row's Message describes the driver's photo rather than quoting the driver: the message, or the latest. */
export function rowMessageIsPhoto(row: DispatchTableRow): boolean {
  switch (row.kind) {
    case "message":
      return row.item.photo;
    case "task":
      return row.item?.photo ?? false;
    case "driver":
      return row.driver.recentSource === "photo";
  }
}

/** Whether anyone has read what the row is about: the message, or the driver's channel. Painted as the row tint. */
export function rowUnread(row: DispatchTableRow): boolean {
  switch (row.kind) {
    case "message":
      return row.item.unread;
    case "task":
      return row.item?.unread ?? false;
    case "driver":
      return row.driver.unread === true;
  }
}

/**
 * Whether the row still waits on a dispatcher: the message, any message on the channel, or the task while it is open,
 * which a task row always is. Painted as the red dot.
 */
export function rowUnacknowledged(row: DispatchTableRow): boolean {
  switch (row.kind) {
    case "message":
      return !row.item.acknowledged;
    case "task":
      return true;
    case "driver":
      return row.unacknowledgedCount > 0;
  }
}

/**
 * The rows showing the red dot, one id per row: a driver row counts once however many of its messages wait.
 */
export function unacknowledgedRowIds(rows: readonly DispatchTableRow[]): string[] {
  return rows.filter(rowUnacknowledged).map((row) => row.id);
}

/** The feed a reply to the row goes to and its History opens: the message's feed, or the driver's alias feed. */
export function rowFeedId(row: DispatchTableRow): string | null {
  switch (row.kind) {
    case "message":
      return row.item.feedId;
    case "task":
      return row.item?.feedId ?? row.driver.feedId;
    case "driver":
      return row.driver.feedId;
  }
}

/**
 * The open task a row carries, for the chip: a task row's own, a message row's item's. A driver row in the Pinned
 * Tasks section carries the task most recently pinned to this board among those it stands for; any other driver row
 * carries the newest open task leading to the driver. Null when there is none.
 *
 * @param row - The row.
 * @param fold - The workspace's open tasks.
 * @param shelfTasksByRow - The tasks each shelf row stands for, by row id; none off the shelf.
 */
export function rowTask(
  row: DispatchTableRow,
  fold: TaskFold,
  shelfTasksByRow: ReadonlyMap<string, readonly OpenTask[]>,
): OpenTask | null {
  switch (row.kind) {
    case "task":
      return row.task;
    case "message":
      return row.item.task ?? null;
    case "driver":
      return driverRowTask(row.driver.workspaceMembershipId, fold, shelfTasksByRow.get(row.id));
  }
}

/**
 * The task a driver row carries, for its chip and its task columns alike.
 *
 * On the Pinned Tasks shelf, the one pinned to this board last among the driver's shelf tasks; elsewhere, the newest
 * open task leading to the driver, so the row's task line names the task its Time can show. Null when none.
 *
 * @param driverId - The driver's membership id.
 * @param fold - The workspace's open tasks.
 * @param shelfTasks - This board's shelf tasks leading to the driver; none off the shelf.
 */
export function driverRowTask(
  driverId: string,
  fold: TaskFold,
  shelfTasks: readonly OpenTask[] | undefined,
): OpenTask | null {
  if (shelfTasks && shelfTasks.length > 0) return mostRecentlyPinned(shelfTasks);
  return fold.byDriver.get(driverId)?.at(-1) ?? null;
}

/**
 * This board's shelf tasks by each driver they lead to, as a Pinned Tasks driver row stands for them.
 *
 * @param shelf - The board's shelf tasks.
 */
export function shelfTasksByDriver(shelf: readonly OpenTask[]): ReadonlyMap<string, readonly OpenTask[]> {
  const byDriver = new Map<string, OpenTask[]>();
  for (const open of shelf) {
    const drivers = new Set(open.task.subjects.flatMap(({ workspaceMembershipId: id }) => (id === null ? [] : [id])));
    for (const driver of drivers) byDriver.set(driver, [...(byDriver.get(driver) ?? []), open]);
  }
  return byDriver;
}

/** The task pinned last; a task with no pin counts as pinned when it was made. */
function mostRecentlyPinned(tasks: readonly OpenTask[]): OpenTask | null {
  const pinnedAt = ({ task, pin }: OpenTask) => pin ?? task;
  return tasks.reduce<OpenTask | null>(
    (latest, open) => (latest === null || byLogOrder(pinnedAt(open), pinnedAt(latest)) > 0 ? open : latest),
    null,
  );
}
